import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useFilters } from "@/context/FiltersContext";
import { CalendarRange, ChevronDown } from "lucide-react";

const PRESETS = [
  { k: "this_week", l: "This week" },
  { k: "next_week", l: "Next week" },
  { k: "this_month", l: "This month" },
  { k: "upcoming", l: "Upcoming (60 days)" },
  { k: "rest2026", l: "Rest of 2026" },
  { k: "season2027", l: "2027 Season" },
  { k: "all", l: "All dates" },
];

const labelFor = (dr) => {
  if (dr.key === "custom" && dr.from && dr.to) {
    const f = new Date(dr.from + "T00:00:00"), t = new Date(dr.to + "T00:00:00");
    const isMonth = f.getDate() === 1 && f.getFullYear() === t.getFullYear() && f.getMonth() === t.getMonth() && t.getDate() === new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
    if (isMonth) return f.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    return `${dr.from} → ${dr.to}`;
  }
  return (PRESETS.find((p) => p.k === dr.key) || PRESETS.find((p) => p.k === "all")).l;
};

// Shared season/date-range control for Map, List and Calendar. Selection is stored in
// FiltersContext so it carries across every view. The menu renders through a portal with
// fixed positioning so it is never clipped by a scrolling/overflow-hidden header.
export function DateRangeButtons({ dark = false, align = "right" }) {
  const { dateRange, setDateRange, setCustomRange } = useFilters();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [from, setFrom] = useState(dateRange.from || "");
  const [to, setTo] = useState(dateRange.to || "");
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 240;
    let left = align === "left" ? r.left : r.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setPos({ top: r.bottom + 6, left });
  };
  const toggle = () => { if (!open) place(); setOpen((v) => !v); };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]); // eslint-disable-line

  const pick = (k) => { setDateRange(k); setOpen(false); };
  const applyCustom = () => { if (from && to) { setCustomRange(from, to); setOpen(false); } };
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const pickMonth = (y, m) => { setCustomRange(iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))); setOpen(false); };
  const monthOpts = [];
  { const t = new Date(); for (let i = 0; i < 12; i++) { const d = new Date(t.getFullYear(), t.getMonth() + i, 1); monthOpts.push({ y: d.getFullYear(), m: d.getMonth() }); } }
  const isMonthActive = (y, m) => {
    if (dateRange.key !== "custom" || !dateRange.from) return false;
    const f = new Date(dateRange.from + "T00:00:00");
    return f.getFullYear() === y && f.getMonth() === m && f.getDate() === 1;
  };

  return (
    <div className="relative shrink-0" data-testid="date-range-buttons">
      <div className="flex items-center gap-1.5">
        <button ref={btnRef} onClick={toggle} data-testid="date-range-trigger" title="Change date range"
          className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1.5 rounded-md border transition-colors ${
            dark ? "bg-white/5 text-slate-200 border-white/15 hover:border-white/30" : "bg-white text-slate-700 border-[#D7DBE0] hover:border-slate-400"}`}>
          <CalendarRange className="w-4 h-4 text-[#1D4ED8]" />
          {dateRange.key !== "custom" && <span className="max-w-[88px] truncate">{labelFor(dateRange)}</span>}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && createPortal(
        <div ref={menuRef} style={{ position: "fixed", top: pos.top, left: pos.left, width: 240 }}
          className={`z-[3000] max-h-[70vh] overflow-y-auto rounded-lg border shadow-xl p-1.5 ${dark ? "bg-[#161B22] border-white/10" : "bg-white border-[#E4E7EB]"}`} data-testid="date-range-menu">
          {PRESETS.map((p) => {
            const active = (dateRange.key || "all") === p.k;
            return (
              <button key={p.k} onClick={() => pick(p.k)} data-testid={`date-range-${p.k}`}
                className={`w-full text-left text-[12px] font-medium px-2.5 py-1.5 rounded-md ${
                  active ? "bg-[#1D4ED8] text-white" : dark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-[#F1F3F5]"}`}>
                {p.l}
              </button>
            );
          })}

          <div className={`mt-1 pt-2 border-t ${dark ? "border-white/10" : "border-[#EEF0F2]"}`}>
            <div className={`text-[10px] font-bold uppercase tracking-wider px-1 mb-1 ${dark ? "text-slate-500" : "text-slate-400"}`}>Jump to month</div>
            <div className="grid grid-cols-2 gap-1">
              {monthOpts.map((o) => {
                const active = isMonthActive(o.y, o.m);
                return (
                  <button key={`${o.y}-${o.m}`} onClick={() => pickMonth(o.y, o.m)} data-testid={`date-range-month-${o.y}-${String(o.m + 1).padStart(2, "0")}`}
                    className={`text-left text-[12px] font-medium px-2.5 py-1.5 rounded-md ${
                      active ? "bg-[#1D4ED8] text-white" : dark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-[#F1F3F5]"}`}>
                    {new Date(o.y, o.m, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" })}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`mt-1 pt-2 border-t ${dark ? "border-white/10" : "border-[#EEF0F2]"}`}>
            <div className={`text-[10px] font-bold uppercase tracking-wider px-1 mb-1 ${dark ? "text-slate-500" : "text-slate-400"}`}>Custom range</div>
            <div className="flex items-center gap-1.5 px-1">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="date-range-custom-from"
                className={`flex-1 min-w-0 text-[11px] rounded-md px-1.5 py-1 border ${dark ? "bg-white/5 text-slate-200 border-white/15" : "bg-white text-slate-700 border-[#D7DBE0]"}`} />
              <span className={dark ? "text-slate-500 text-xs" : "text-slate-400 text-xs"}>→</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="date-range-custom-to"
                className={`flex-1 min-w-0 text-[11px] rounded-md px-1.5 py-1 border ${dark ? "bg-white/5 text-slate-200 border-white/15" : "bg-white text-slate-700 border-[#D7DBE0]"}`} />
            </div>
            <button onClick={applyCustom} data-testid="date-range-custom-apply"
              className="w-full mt-1.5 text-[11px] font-semibold py-1.5 rounded-md bg-[#1D4ED8] text-white hover:bg-[#1740b0] disabled:opacity-40"
              disabled={!from || !to}>Apply custom range</button>
          </div>
        </div>, document.body)}
    </div>
  );
}
